import { styled, useTheme } from '@mui/material';
import React, { useRef } from 'react';
import { AnimationDot } from '../Animation';
import { ShoppingBagIcon } from '../../icons';
import { ButtonName } from '../../theme/componentNames';
import { Button } from './Button';
import { parseThemeUrl } from '../../utils/stringUtils';
import { InlineSvgIcon } from '../Image/InlineSvgIcon';
import { useBagWords } from '../../utils/hooks/useBagWords';

const CartCountContainer = styled('div', { label: 'CartCountContainer' })(
  ({ bottomOffset }: { bottomOffset: number }) => ({
    position: 'absolute',
    bottom: +bottomOffset,
    fontWeight: 600,
    lineHeight: 1.4,
  }),
);

export const CustomDtcBagButton = ({
  cartCount,
  handleClick,
  mobile,
}: {
  cartCount?: number;
  handleClick?: () => void;
  mobile?: boolean;
}) => {
  const buttonRef = useRef<HTMLButtonElement>(null);
  const theme = useTheme();
  const { Bag } = useBagWords();
  const content = mobile ? theme.content?.cartButton?.signedOutMobile : theme.content?.cartButton?.signedOutDesktop;
  const customImage = parseThemeUrl(content?.customIconStartSrc ?? content?.customIconEndSrc);
  const iconLabel = content?.iconLabel;
  const enableAnimationDot = mobile
    ? !!theme?.settings?.featureFlags?.bagAnimationDotMobile
    : !!theme?.settings?.featureFlags?.bagAnimationDot;
  const enableCartCount = mobile
    ? !!theme?.settings?.featureFlags?.cartCountNumberMobile
    : !!theme?.settings?.featureFlags?.cartCountNumber;
  const countNumberYOffset = theme.settings?.fonts?.numericFontOffsetY ?? 4;
  const countNumberSize = theme.componentCSSOverrides?.[ButtonName.BagButton]?.[mobile ? 'xs' : 'md']?.fontSize ?? 8;

  const bagButtonStyleOverride = theme.componentCSSOverrides?.[ButtonName.BagButton]?.[mobile ? 'xs' : 'md'];

  // Use the brand's configured button size for the icon box. This prevents the
  // SVG from rendering at its intrinsic viewBox dimensions when the host slot
  // is large (e.g. height:100% of the header). Fallback to 24px when unconfigured.
  const iconSize: number | string =
    (bagButtonStyleOverride?.width ?? bagButtonStyleOverride?.height ?? 24) as number | string;

  const textButton = !customImage;

  if (textButton) {
    const text = content?.text;
    return (
      <Button
        disableResponsiveStyles
        sx={{ ...bagButtonStyleOverride, height: 'auto' }}
        variant="text"
        color="default"
        buttonName={ButtonName.BagButton}
        onClick={handleClick}
      >
        <span>{text && cartCount && enableCartCount ? `${text} (${cartCount})` : text}</span>
      </Button>
    );
  }

  return (
    <Button
      ref={buttonRef}
      variant="icon"
      color="default"
      buttonName={ButtonName.BagButton}
      onClick={handleClick}
      // overflow:visible so the label can paint outside the button/slot bounds.
      // The host page's slot ancestors are made overflow:visible via the injected
      // BC_SLOT_OVERFLOW_STYLE_ID stylesheet in CheckoutShopApp.
      style={{
        display: 'flex',
        flexDirection: iconLabel ? 'column' : 'row',
        alignItems: 'center',
        justifyContent: 'center',
        overflow: 'visible',
      }}
      sx={bagButtonStyleOverride}
    >
      {/* Icon box: explicitly sized so large-viewBox SVGs don't expand to fill
          the host slot. The brand's buttonStyleOverride width/height is used as
          the icon size, with 24px as the fallback. */}
      <div
        style={{
          position: 'relative',
          flexShrink: 0,
          width: iconSize,
          height: iconSize,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        {cartCount ? (
          <>
            {enableAnimationDot && (
              <AnimationDot
                className="bc-animation-dot"
                size={8}
                topOffset={0}
                rightOffset={0}
                color={'accent'}
                data-theme-color="accent"
              />
            )}
            {enableCartCount && (
              <CartCountContainer
                className="bc-cart-count-container"
                bottomOffset={countNumberYOffset}
                sx={{ fontSize: countNumberSize }}
              >
                {cartCount}
              </CartCountContainer>
            )}
          </>
        ) : null}
        {customImage ? (
          <InlineSvgIcon
            src={customImage}
            alt={`${Bag} Icon`}
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
          />
        ) : (
          <ShoppingBagIcon />
        )}
      </div>
      {iconLabel && (
        <span
          className="bc-icon-label"
          style={{
            fontSize: 11,
            lineHeight: 1,
            fontWeight: 400,
            color: 'currentColor',
            whiteSpace: 'nowrap',
            marginTop: 7,
            pointerEvents: 'none',
          }}
        >
          {iconLabel}
        </span>
      )}
    </Button>
  );
};
